import React, { useState, useEffect, useCallback } from "react"
import { useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPen } from "@fortawesome/free-solid-svg-icons"
import { URL } from "../../../store/actions/depannageActions"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import { tableStyles } from "../../styles/tableStyles"
import Loader from "../../basics/Loader"
import CustomModal from "../../basics/interface/CustomModal"

const UNIT = 22
const LIGNE_X = 10
const TRAIT = `1px solid ${COLORS.borderStrong}`

const styles = {
	header: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: 16,
	},
	title: { fontSize: 15, fontWeight: 600, color: COLORS.text },
	count: { fontSize: 13, color: COLORS.textLight },
	empty: {
		...tableStyles.empty,
		padding: "40px 16px",
		color: COLORS.textLight,
		fontSize: 14,
	},
	searchRow: {
		display: "flex",
		gap: 10,
		alignItems: "center",
		marginBottom: 16,
	},
	arbreCell: { ...tableStyles.td, padding: "0 16px 0 12px" },
	arbreLigne: { display: "flex", alignItems: "center", minHeight: 44 },
	unit: {
		position: "relative",
		width: UNIT,
		alignSelf: "stretch",
		flexShrink: 0,
	},
	traverse: {
		position: "absolute",
		top: 0,
		bottom: 0,
		left: LIGNE_X,
		borderLeft: TRAIT,
	},
	horizontal: {
		position: "absolute",
		top: "50%",
		left: LIGNE_X,
		right: 2,
		borderTop: TRAIT,
	},
	coude: {
		position: "absolute",
		top: 0,
		height: "50%",
		left: LIGNE_X,
		right: 2,
		borderLeft: TRAIT,
		borderBottom: TRAIT,
		borderBottomLeftRadius: 8,
	},
	chevron: {
		width: 20,
		height: 20,
		padding: 0,
		border: "none",
		background: "transparent",
		cursor: "pointer",
		color: COLORS.textMuted,
		fontSize: 11,
		lineHeight: "20px",
		marginRight: 6,
		flexShrink: 0,
	},
	slotFeuille: {
		width: 20,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		marginRight: 6,
		flexShrink: 0,
	},
	pointFeuille: {
		width: 5,
		height: 5,
		borderRadius: 99,
		backgroundColor: COLORS.borderStrong,
	},
	groupeLabel: { fontWeight: 600 },
	badgeCount: {
		fontSize: 11,
		padding: "2px 8px",
		borderRadius: 99,
		background: COLORS.accentLight,
		color: COLORS.accent,
		marginLeft: 6,
	},
}

const emptyAdresse = { rue: "", code_postal: "", localite: "", pays: "Suisse" }

const Row = ({ children, index, onClick }) => {
	const [hover, setHover] = useState(false)
	const bg = hover ? tableStyles.rowHover : tableStyles.rowBg(index)
	return (
		<tr
			onDoubleClick={onClick}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				backgroundColor: bg,
				cursor: "pointer",
				transition: "background-color 0.12s",
			}}
		>
			{children}
		</tr>
	)
}

const AdressesV2 = () => {
	const { token } = useSelector((s) => s.navInfo.login)
	const [groupes, setGroupes] = useState([])
	const [loading, setLoading] = useState(true)
	const [search, setSearch] = useState("")
	const [expanded, setExpanded] = useState({})
	const [children, setChildren] = useState({})
	const [childLoading, setChildLoading] = useState({})
	const [editing, setEditing] = useState(null)
	const [form, setForm] = useState(emptyAdresse)
	const [saving, setSaving] = useState(false)
	const [deleting, setDeleting] = useState(false)
	const [confirmDelete, setConfirmDelete] = useState(false)
	const [erreur, setErreur] = useState("")
	const [editingGroupe, setEditingGroupe] = useState(null)
	const [groupeForm, setGroupeForm] = useState({
		code_postal: "",
		localite: "",
	})
	const [groupeSaving, setGroupeSaving] = useState(false)
	const [groupeResult, setGroupeResult] = useState(null)

	const fetchGroupes = useCallback(() => {
		setLoading(true)
		const qs =
			search.length >= 2 ? `&search=${encodeURIComponent(search)}` : ""
		fetch(`${URL}api/adressesv2?groupes=1${qs}`, {
			headers: { Authorization: `Bearer ${token}` },
		})
			.then((r) => r.json())
			.then((data) => {
				setGroupes(data)
				setLoading(false)
			})
			.catch(() => setLoading(false))
	}, [token, search])

	useEffect(() => {
		fetchGroupes()
	}, [fetchGroupes])

	const groupeKey = (g) => `${g.code_postal}|${g.localite}`

	const toggleGroupe = (g) => {
		const key = groupeKey(g)
		if (expanded[key]) {
			setExpanded((p) => ({ ...p, [key]: false }))
			return
		}
		setExpanded((p) => ({ ...p, [key]: true }))
		if (children[key]) return
		setChildLoading((p) => ({ ...p, [key]: true }))
		fetch(
			`${URL}api/adressesv2?code_postal=${encodeURIComponent(g.code_postal)}&localite=${encodeURIComponent(g.localite)}`,
			{ headers: { Authorization: `Bearer ${token}` } },
		)
			.then((r) => r.json())
			.then((data) => {
				setChildren((p) => ({ ...p, [key]: data }))
				setChildLoading((p) => ({ ...p, [key]: false }))
			})
			.catch(() => setChildLoading((p) => ({ ...p, [key]: false })))
	}

	const refreshGroupe = (g) => {
		const key = groupeKey(g)
		fetch(
			`${URL}api/adressesv2?code_postal=${encodeURIComponent(g.code_postal)}&localite=${encodeURIComponent(g.localite)}`,
			{ headers: { Authorization: `Bearer ${token}` } },
		)
			.then((r) => r.json())
			.then((data) => setChildren((p) => ({ ...p, [key]: data })))
			.catch(() => {})
		fetchGroupes()
	}

	const openEdit = (adr, groupe) => {
		setEditing({ ...adr, _groupe: groupe })
		setForm({
			rue: adr.rue || "",
			code_postal: adr.code_postal || "",
			localite: adr.localite || "",
			pays: adr.pays || "",
		})
		setConfirmDelete(false)
		setErreur("")
	}

	const openNewInGroupe = (g) => {
		setEditing({ _groupe: g })
		setForm({
			rue: "",
			code_postal: g.code_postal,
			localite: g.localite,
			pays: "Suisse",
		})
		setConfirmDelete(false)
		setErreur("")
	}

	const openNew = () => {
		setEditing({})
		setForm({ ...emptyAdresse })
		setConfirmDelete(false)
		setErreur("")
	}

	const handleSave = () => {
		setSaving(true)
		setErreur("")
		const isNew = !editing.id
		fetch(`${URL}api/adressesv2`, {
			method: isNew ? "POST" : "PUT",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify(isNew ? form : { id: editing.id, ...form }),
		})
			.then((r) => r.json())
			.then((data) => {
				setSaving(false)
				if (data.message || data.detail) {
					setErreur(data.message || data.detail)
					return
				}
				const g = editing._groupe
				setEditing(null)
				setErreur("")
				if (g) refreshGroupe(g)
				else fetchGroupes()
			})
			.catch(() => {
				setSaving(false)
				setErreur("Échec de la connexion au serveur.")
			})
	}

	const handleDelete = () => {
		setDeleting(true)
		setErreur("")
		fetch(`${URL}api/adressesv2`, {
			method: "DELETE",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ id: editing.id }),
		})
			.then((r) => r.json())
			.then((data) => {
				setDeleting(false)
				if (data.message || data.detail) {
					setErreur(data.message || data.detail)
					setConfirmDelete(false)
					return
				}
				const g = editing._groupe
				setEditing(null)
				setErreur("")
				if (g) refreshGroupe(g)
				else fetchGroupes()
			})
			.catch(() => {
				setDeleting(false)
				setErreur("Échec de la connexion au serveur.")
			})
	}

	const openEditGroupe = (g) => {
		setEditingGroupe(g)
		setGroupeForm({
			code_postal: g.code_postal,
			localite: g.localite,
		})
		setGroupeResult(null)
		setErreur("")
	}

	const groupeCollision = editingGroupe
		? groupes.find(
				(g) =>
					g.code_postal === groupeForm.code_postal &&
					g.localite === groupeForm.localite &&
					(g.code_postal !== editingGroupe.code_postal ||
						g.localite !== editingGroupe.localite),
			)
		: null

	const handleGroupeSave = () => {
		setGroupeSaving(true)
		setErreur("")
		fetch(`${URL}api/adressesv2`, {
			method: "PATCH",
			headers: {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json",
			},
			body: JSON.stringify({
				source_code_postal: editingGroupe.code_postal,
				source_localite: editingGroupe.localite,
				cible_code_postal: groupeForm.code_postal,
				cible_localite: groupeForm.localite,
			}),
		})
			.then((r) => r.json())
			.then((data) => {
				setGroupeSaving(false)
				if (data.message || data.detail) {
					setErreur(data.message || data.detail)
					return
				}
				setErreur("")
				setGroupeResult(data)
				setExpanded({})
				setChildren({})
				fetchGroupes()
			})
			.catch(() => {
				setGroupeSaving(false)
				setErreur("Échec de la connexion au serveur.")
			})
	}

	const lignes = []
	let idx = 0
	groupes.forEach((g, gi) => {
		const key = groupeKey(g)
		const isLast = gi === groupes.length - 1
		const isOpen = expanded[key]
		lignes.push({ type: "groupe", g, key, isLast, idx: idx++ })
		if (isOpen) {
			const kids = children[key] || []
			const isLoadingKids = childLoading[key]
			if (isLoadingKids && kids.length === 0) {
				lignes.push({
					type: "loading",
					g,
					key,
					isLastParent: isLast,
					idx: idx++,
				})
			} else {
				kids.forEach((adr, ai) => {
					lignes.push({
						type: "adresse",
						adr,
						g,
						isLastChild: ai === kids.length - 1,
						isLastParent: isLast,
						idx: idx++,
					})
				})
				lignes.push({
					type: "add",
					g,
					isLastParent: isLast,
					idx: idx++,
				})
			}
		}
	})

	return (
		<>
			{editing && (
				<CustomModal
					title={
						editing.id ? "Modifier l'adresse" : "Nouvelle adresse"
					}
					onClose={() => setEditing(null)}
					buttons={[
						...(editing.id && !confirmDelete
							? [
									{
										title: "Supprimer",
										styleName: "danger",
										onClick: () => setConfirmDelete(true),
										disabled: deleting,
									},
								]
							: []),
						...(confirmDelete
							? [
									{
										title: deleting
											? "Suppression…"
											: "Confirmer la suppression",
										styleName: "danger",
										onClick: handleDelete,
										disabled: deleting,
									},
									{
										title: "Annuler",
										styleName: "secondary",
										onClick: () => setConfirmDelete(false),
									},
								]
							: [
									{
										title: saving
											? "Enregistrement…"
											: "Enregistrer",
										styleName: "primary",
										onClick: handleSave,
										disabled:
											saving ||
											!form.rue.trim() ||
											!form.code_postal.trim(),
									},
									{
										title: "Annuler",
										styleName: "secondary",
										onClick: () => setEditing(null),
									},
								]),
					]}
				>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: 12,
						}}
					>
						<div style={gs.fieldRow}>
							<label style={gs.label}>Rue</label>
							<input
								style={gs.input}
								value={form.rue}
								onChange={(e) =>
									setForm({ ...form, rue: e.target.value })
								}
							/>
						</div>
						<div style={gs.fieldRow}>
							<label style={gs.label}>Code postal</label>
							<input
								style={gs.input}
								value={form.code_postal}
								onChange={(e) =>
									setForm({
										...form,
										code_postal: e.target.value,
									})
								}
							/>
						</div>
						<div style={gs.fieldRow}>
							<label style={gs.label}>Localité</label>
							<input
								style={gs.input}
								value={form.localite}
								onChange={(e) =>
									setForm({
										...form,
										localite: e.target.value,
									})
								}
							/>
						</div>
						<div style={gs.fieldRow}>
							<label style={gs.label}>Pays</label>
							<input
								style={gs.input}
								value={form.pays}
								onChange={(e) =>
									setForm({
										...form,
										pays: e.target.value,
									})
								}
							/>
						</div>
						{editing.id && (
							<div
								style={{
									fontSize: 12,
									color: COLORS.textMuted,
									lineHeight: 1.5,
								}}
							>
								Cette adresse est partagée. La modifier corrige
								toutes les fiches et entités qui la référencent.
							</div>
						)}
						{erreur && (
							<div
								style={{
									padding: "10px 12px",
									backgroundColor: COLORS.dangerLight,
									borderRadius: 8,
									fontSize: 13,
									color: COLORS.danger,
									lineHeight: 1.5,
								}}
							>
								{erreur}
							</div>
						)}
					</div>
				</CustomModal>
			)}

			{editingGroupe && (
				<CustomModal
					title={
						groupeResult
							? "Résultat"
							: groupeCollision
								? "Fusion de groupes"
								: "Modifier le groupe"
					}
					onClose={() => {
						setEditingGroupe(null)
						setGroupeResult(null)
					}}
					buttons={
						groupeResult
							? [
									{
										title: "Fermer",
										styleName: "primary",
										onClick: () => {
											setEditingGroupe(null)
											setGroupeResult(null)
										},
									},
								]
							: [
									{
										title: groupeSaving
											? "Enregistrement…"
											: groupeCollision
												? `Fusionner ${editingGroupe.count} adresse(s)`
												: "Enregistrer",
										styleName: groupeCollision
											? "danger"
											: "primary",
										onClick: handleGroupeSave,
										disabled:
											groupeSaving ||
											!groupeForm.code_postal.trim() ||
											(groupeForm.code_postal ===
												editingGroupe.code_postal &&
												groupeForm.localite ===
													editingGroupe.localite),
									},
									{
										title: "Annuler",
										styleName: "secondary",
										onClick: () => {
											setEditingGroupe(null)
											setGroupeResult(null)
										},
									},
								]
					}
				>
					{groupeResult ? (
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								gap: 8,
								padding: "4px 0",
								fontSize: 14,
							}}
						>
							{groupeResult.deplacees > 0 && (
								<div>
									{groupeResult.deplacees} adresse(s)
									déplacée(s)
								</div>
							)}
							{groupeResult.fusionnees > 0 && (
								<div>
									{groupeResult.fusionnees} adresse(s)
									fusionnée(s) (doublons absorbés)
								</div>
							)}
						</div>
					) : (
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								gap: 12,
							}}
						>
							<div style={gs.fieldRow}>
								<label style={gs.label}>Code postal</label>
								<input
									style={gs.input}
									value={groupeForm.code_postal}
									onChange={(e) =>
										setGroupeForm({
											...groupeForm,
											code_postal: e.target.value,
										})
									}
								/>
							</div>
							<div style={gs.fieldRow}>
								<label style={gs.label}>Localité</label>
								<input
									style={gs.input}
									value={groupeForm.localite}
									onChange={(e) =>
										setGroupeForm({
											...groupeForm,
											localite: e.target.value,
										})
									}
								/>
							</div>
							{groupeCollision && (
								<div
									style={{
										padding: "10px 12px",
										backgroundColor: COLORS.dangerLight,
										borderRadius: 8,
										fontSize: 13,
										color: COLORS.danger,
										lineHeight: 1.5,
									}}
								>
									{groupeForm.code_postal}{" "}
									{groupeForm.localite} existe déjà (
									{groupeCollision.count} adresse(s)) — les{" "}
									{editingGroupe.count} adresse(s) du groupe
									actuel seront fusionnées dedans.
								</div>
							)}
							{erreur && (
								<div
									style={{
										padding: "10px 12px",
										backgroundColor: COLORS.dangerLight,
										borderRadius: 8,
										fontSize: 13,
										color: COLORS.danger,
										lineHeight: 1.5,
									}}
								>
									{erreur}
								</div>
							)}
						</div>
					)}
				</CustomModal>
			)}

			<div style={styles.header}>
				<div
					style={{
						display: "flex",
						alignItems: "baseline",
						gap: 10,
					}}
				>
					<span style={styles.title}>Adresses</span>
					<span style={styles.count}>{groupes.length} groupe(s)</span>
				</div>
				<button style={gs.btnPrimary} onClick={openNew}>
					+ Nouvelle adresse
				</button>
			</div>

			<div style={styles.searchRow}>
				<input
					style={{ ...gs.input, flex: 1 }}
					placeholder="Filtrer par NPA ou localité…"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>
			</div>

			<div style={tableStyles.container}>
				{loading && groupes.length === 0 ? (
					<Loader variant="ring" message="Chargement…" />
				) : groupes.length === 0 ? (
					<div style={styles.empty}>Aucun groupe trouvé.</div>
				) : (
					<table style={tableStyles.table}>
						<thead>
							<tr>
								<th style={tableStyles.th}>Adresse</th>
								<th style={tableStyles.th}>Pays</th>
								<th style={tableStyles.th}>Usages</th>
							</tr>
						</thead>
						<tbody>
							{lignes.map((l) => {
								if (l.type === "groupe") {
									return (
										<Row
											key={`g-${l.key}`}
											index={l.idx}
											onClick={() => toggleGroupe(l.g)}
										>
											<td style={styles.arbreCell}>
												<div style={styles.arbreLigne}>
													<button
														style={styles.chevron}
														onClick={(e) => {
															e.stopPropagation()
															toggleGroupe(l.g)
														}}
														onDoubleClick={(e) =>
															e.stopPropagation()
														}
													>
														<span
															style={{
																display:
																	"inline-block",
																transition:
																	"transform 0.15s",
																transform:
																	expanded[
																		l.key
																	]
																		? "none"
																		: "rotate(-90deg)",
															}}
														>
															▾
														</span>
													</button>
													<span
														style={
															styles.groupeLabel
														}
													>
														{l.g.code_postal}{" "}
														{l.g.localite || "—"}
													</span>
													<span
														style={
															styles.badgeCount
														}
													>
														{l.g.count}
													</span>
													<button
														style={{
															...styles.chevron,
															marginLeft: 6,
															fontSize: 10,
															color: COLORS.textLight,
														}}
														title="Modifier le groupe"
														onClick={(e) => {
															e.stopPropagation()
															openEditGroupe(l.g)
														}}
														onDoubleClick={(e) =>
															e.stopPropagation()
														}
													>
														<FontAwesomeIcon
															icon={faPen}
														/>
													</button>
												</div>
											</td>
											<td style={tableStyles.td} />
											<td style={tableStyles.td} />
										</Row>
									)
								}
								if (l.type === "loading") {
									return (
										<tr key={`ld-${l.key}`}>
											<td
												style={styles.arbreCell}
												colSpan={3}
											>
												<div style={styles.arbreLigne}>
													<span style={styles.unit}>
														{l.isLastParent ? (
															<span
																style={
																	styles.coude
																}
															/>
														) : (
															<>
																<span
																	style={
																		styles.traverse
																	}
																/>
																<span
																	style={
																		styles.horizontal
																	}
																/>
															</>
														)}
													</span>
													<span
														style={{
															fontSize: 12,
															color: COLORS.textLight,
														}}
													>
														Chargement…
													</span>
												</div>
											</td>
										</tr>
									)
								}
								if (l.type === "add") {
									const isLast = true
									return (
										<tr key={`add-${groupeKey(l.g)}`}>
											<td
												style={styles.arbreCell}
												colSpan={3}
											>
												<div style={styles.arbreLigne}>
													<span style={styles.unit}>
														{l.isLastParent ? null : (
															<span
																style={
																	styles.traverse
																}
															/>
														)}
													</span>
													<span style={styles.unit}>
														{isLast ? (
															<span
																style={
																	styles.coude
																}
															/>
														) : (
															<>
																<span
																	style={
																		styles.traverse
																	}
																/>
																<span
																	style={
																		styles.horizontal
																	}
																/>
															</>
														)}
													</span>
													<button
														style={{
															...gs.btnSecondary,
															padding: "4px 10px",
															fontSize: 12,
														}}
														onClick={() =>
															openNewInGroupe(l.g)
														}
													>
														+ Ajouter
													</button>
												</div>
											</td>
										</tr>
									)
								}
								return (
									<Row
										key={`a-${l.adr.id}`}
										index={l.idx}
										onClick={() => openEdit(l.adr, l.g)}
									>
										<td style={styles.arbreCell}>
											<div style={styles.arbreLigne}>
												<span style={styles.unit}>
													{l.isLastParent ? null : (
														<span
															style={
																styles.traverse
															}
														/>
													)}
												</span>
												<span style={styles.unit}>
													{l.isLastChild ? (
														<span
															style={styles.coude}
														/>
													) : (
														<>
															<span
																style={
																	styles.traverse
																}
															/>
															<span
																style={
																	styles.horizontal
																}
															/>
														</>
													)}
												</span>
												<span
													style={styles.slotFeuille}
												>
													<span
														style={
															styles.pointFeuille
														}
													/>
												</span>
												{l.adr.rue || "—"}
											</div>
										</td>
										<td style={tableStyles.td}>
											{l.adr.pays || "—"}
										</td>
										<td style={tableStyles.td}>
											{l.adr.usages ?? "—"}
										</td>
									</Row>
								)
							})}
						</tbody>
					</table>
				)}
			</div>
		</>
	)
}

export default AdressesV2
